import { useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Divider, Typography } from "@mui/material";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import __ from "src/utils/Translations";
import formatDateRangePreview from "src/utils/Company/Absences/formatDateRangePreview";
import {
  useIsAbsenceRequestActionPending,
  useRejectAbsenceRequests,
} from "src/Pages/Company/Absences/api/absenceRequests.queries";
import { useModalActionsPending } from "src/utils/hooks/absences/useModalActionsPending";
import { StatusChip } from "src/Pages/Company/Absences/AbsenceRequest/StatusChip";
import EmployeeDetails from "src/Pages/Company/Absences/EmployeeDetails";
import {
  IAbsenceTimes,
  IAbsenceRequestListItem,
  IEmployeeDetails,
  Status,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";

interface IConfirmRejection {
  absenceRequestId: string;
  absenceTypeName: string;
  employeeFirstName: string;
  employeeLastName: string;
  eTag: string;
}

interface IConfirmRejectionExtended {
  absenceRequestId: string;
  absenceTypeName: string;
  employee: IEmployeeDetails;
  absenceTimes: IAbsenceTimes[];
  status: Status;
  previewLink: string;
  eTag: string;
}

export const useConfirmRejection = () => {
  const history = useHistory();
  const { setModal, resetModal } = useContext(GlobalModalContext);

  const { mutate: rejectAbsenceRequests } =
    useRejectAbsenceRequests(resetModal);

  const isPending = useIsAbsenceRequestActionPending();
  const modalId = useModalActionsPending(isPending);

  const confirmRejection = useCallback(
    ({
      absenceRequestId,
      absenceTypeName,
      employeeFirstName,
      employeeLastName,
      eTag,
    }: IConfirmRejection) => {
      if (typeof setModal === "function") {
        setModal({
          id: modalId,
          open: true,
          disableOutsideClick: isPending,
          disableExitButton: isPending,
          title: <>{__("Czy na pewno chcesz odrzucić ten wniosek?")}</>,
          description: `${__("Czy na pewno chcesz odrzucić ten wniosek o")} ${{
            absenceTypeName,
          }} ${employeeFirstName} ${employeeLastName}? ${__(
            "Tej operacji nie można cofnąć."
          )}`,
          actions: [
            {
              label: __("Anuluj"),
              variant: "outlined",
              disabled: isPending,
              callbackFn: () => {
                resetModal();
              },
            },
            {
              label: __("Odrzuć wniosek"),
              color: "error",
              variant: "contained",
              disabled: isPending,
              callbackFn: () => {
                rejectAbsenceRequests({
                  absenceRequests: [absenceRequestId!],
                  absenceRequestsWithETags: [
                    {
                      absenceRequestId,
                      eTag,
                    },
                  ],
                });
              },
            },
          ],
        });
      }
    },
    [isPending, modalId, rejectAbsenceRequests, resetModal, setModal]
  );

  const confirmRejectionExtended = useCallback(
    ({
      absenceRequestId,
      absenceTypeName,
      employee,
      absenceTimes,
      status,
      previewLink,
      eTag,
    }: IConfirmRejectionExtended) => {
      if (typeof setModal === "function") {
        setModal({
          id: modalId,
          open: true,
          disableOutsideClick: isPending,
          disableExitButton: isPending,
          title: (
            <>
              {__("Czy na pewno chcesz odrzucić wniosek")} {employee.firstName}{" "}
              {employee.lastName}?
            </>
          ),
          description: __(
            "Tej operacji nie można cofnąć. Zanim odrzucisz wniosek przejdź do podglądu wniosku i zweryfikuj zawarte w nim dane."
          ),
          customElements: (
            <>
              <EmployeeDetails data={employee} showFk />
              <Typography variant="body2" color="grey.600" sx={{ mb: "1rem" }}>
                {absenceTypeName}
                <br />
                {__("Termin wniosku")}:<br />
                {formatDateRangePreview(absenceTimes)}
              </Typography>
              <StatusChip variant={status} />
            </>
          ),
          actions: [
            {
              label: __("Przejdź do wniosku"),
              variant: "outlined",
              disabled: isPending,
              callbackFn: () => {
                resetModal();
                history.push(previewLink);
              },
            },
            {
              label: __("Odrzuć wniosek"),
              color: "error",
              variant: "contained",
              disabled: isPending,
              callbackFn: () => {
                rejectAbsenceRequests({
                  absenceRequests: [absenceRequestId!],
                  absenceRequestsWithETags: [
                    {
                      absenceRequestId,
                      eTag,
                    },
                  ],
                });
              },
            },
          ],
        });
      }
    },
    [history, isPending, modalId, rejectAbsenceRequests, resetModal, setModal]
  );

  const confirmRejectionMultiple = useCallback(
    (
      absenceRequests: IAbsenceRequestListItem[] = [],
      total: number,
      resetRowSelection: () => void
    ) => {
      const absenceRequestIds = absenceRequests.map(
        (absenceRequest) => absenceRequest.absenceRequestId
      );

      const absenceRequestsWithETags = absenceRequests.map(
        (absenceRequest) => ({
          absenceRequestId: absenceRequest.absenceRequestId,
          eTag: absenceRequest.eTag,
        })
      );

      if (typeof setModal === "function") {
        setModal({
          id: modalId,
          open: true,
          disableOutsideClick: isPending,
          disableExitButton: isPending,
          title: <>{__("Czy na pewno chcesz odrzucić wnioski?")}</>,
          description: __(
            "Tej operacji nie można cofnąć. Zanim odrzucisz wnioski przejdź do podglądu i zweryfikuj zawarte w nich dane."
          ),
          customElements: (
            <>
              {absenceRequests.map((absenceRequest) => (
                <>
                  <EmployeeDetails data={absenceRequest.employee} showFk />
                  <Typography
                    variant="body2"
                    color="grey.600"
                    sx={{ mb: "1rem" }}
                  >
                    {absenceRequest.absenceType.name}
                    <br />
                    {__("Termin wniosku")}:<br />
                    {formatDateRangePreview(absenceRequest.absenceTimes)}
                  </Typography>
                  <StatusChip variant={absenceRequest.status} />
                  <Divider sx={{ mt: "1rem" }} />
                </>
              ))}
              <Typography
                sx={{ textAlign: "center", mt: "1rem", fontWeight: 700 }}
              >
                {__("Wybrano")}: {absenceRequests.length} {__("z")} {total}
              </Typography>
            </>
          ),
          actions: [
            {
              label: __("Anuluj"),
              variant: "outlined",
              disabled: isPending,
              callbackFn: () => {
                resetModal();
              },
            },
            {
              label: __("Odrzuć wszystkie"),
              color: "error",
              variant: "contained",
              disabled: isPending,
              callbackFn: () => {
                rejectAbsenceRequests({
                  absenceRequests: absenceRequestIds,
                  absenceRequestsWithETags,
                });
                resetRowSelection();
              },
            },
          ],
        });
      }
    },
    [isPending, modalId, rejectAbsenceRequests, resetModal, setModal]
  );

  return {
    confirmRejection,
    confirmRejectionExtended,
    confirmRejectionMultiple,
  };
};
